/**
 * RetryQueue — "Retry queue" sub-tab.
 * Failed / retry-eligible outbound faxes with per-item retry, plus a
 * "Auto-retry now" batch action.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw, AlertTriangle, Play } from "lucide-react";
import { faxAPI } from "./faxAPI";
import { formatPhoneNumber } from "../../../lib/utils";
import { NAVY, RED, AMBER, NAVY_HAIRLINE, NAVY_TINT } from "../tokens";
import ViewToggle, { useViewMode } from "../ViewToggle";

export default function RetryQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [view, setView] = useViewMode("comms2-fax-retry-view", "list");

  const load = async () => {
    setLoading(true);
    try {
      const r = await faxAPI.retryQueue();
      setItems(r?.items || r?.faxes || (Array.isArray(r) ? r : []));
    } catch { setItems([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const scan = async () => {
    setBusy(true);
    try {
      const r = await faxAPI.autoRetryScan();
      const n = r?.retried_count ?? r?.retried ?? 0;
      toast.success(n > 0 ? `Retried ${n} fax${n === 1 ? "" : "es"}` : "Nothing eligible to retry right now");
      load();
    } catch (e) { toast.error(e.message || "Retry scan failed"); }
    finally { setBusy(false); }
  };

  const retry = async (fax) => {
    setBusyId(fax.id);
    try { await faxAPI.retry(fax.id); toast.success("Retry queued"); load(); }
    catch (e) { toast.error(e.message || "Retry failed"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <div className="text-[12.5px] text-slate-600">
          {loading ? "Checking retry queue…" : items.length === 0 ? "Retry queue is empty." : `${items.length} fax${items.length === 1 ? "" : "es"} eligible for retry.`}
        </div>
        <div className="flex-1" />
        {items.length > 0 && <ViewToggle view={view} setView={setView} testid="comms2-fax-retry" />}
        <button
          onClick={scan}
          disabled={busy}
          className="h-9 px-3 rounded-md border text-[12.5px] font-semibold flex items-center gap-1.5 disabled:opacity-40 hover:bg-white"
          style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
          data-testid="comms2-fax-retry-scan"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
          Auto-retry now
        </button>
      </div>

      {items.length > 0 && view === "list" && (
        <div className="rounded-lg border overflow-hidden bg-white" style={{ borderColor: NAVY_HAIRLINE }}>
          <div className="grid grid-cols-[minmax(0,1fr)_130px_120px_100px] text-[10.5px] uppercase font-semibold tracking-wider text-slate-500 px-3 py-1.5 border-b" style={{ borderColor: NAVY_HAIRLINE, background: NAVY_TINT }}>
            <div>Recipient · reason</div><div>Attempts</div><div>Last try</div><div className="text-right">Actions</div>
          </div>
          {items.map(f => {
            const attempts = f.retry_attempt ?? f.attempts ?? 0;
            const max = f.max_retries ?? 3;
            const lastTry = f.last_attempted_at || f.updated_at || f.created_at;
            return (
              <div key={f.id} className="grid grid-cols-[minmax(0,1fr)_130px_120px_100px] items-center px-3 py-2 border-b last:border-0 text-[12px]" style={{ borderColor: NAVY_HAIRLINE }} data-testid={`comms2-fax-retry-${f.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate" style={{ color: NAVY }}>
                    {f.to_name || formatPhoneNumber(f.to_number) || "Unknown"}
                  </div>
                  <div className="text-[10.5px] flex items-center gap-1" style={{ color: RED }}>
                    <AlertTriangle className="w-3 h-3" /> {f.error_message || f.failure_reason || f.status || "failed"}
                  </div>
                </div>
                <div className="text-[11.5px] tabular-nums" style={{ color: AMBER }}>{attempts} / {max}</div>
                <div className="text-[11px] text-slate-600 tabular-nums">
                  {lastTry ? new Date(lastTry).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                </div>
                <div className="flex items-center justify-end">
                  <button
                    onClick={() => retry(f)}
                    disabled={busyId === f.id}
                    className="h-7 px-2 rounded text-[11px] border font-medium hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1"
                    style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
                    data-testid={`comms2-fax-retry-btn-${f.id}`}
                  >
                    {busyId === f.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                    Retry
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {items.length > 0 && view === "cards" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="comms2-fax-retry-cards">
          {items.map(f => {
            const attempts = f.retry_attempt ?? f.attempts ?? 0;
            const max = f.max_retries ?? 3;
            const lastTry = f.last_attempted_at || f.updated_at || f.created_at;
            return (
              <div key={f.id} className="rounded-xl bg-white border p-3" style={{ borderColor: NAVY_HAIRLINE, borderTop: `3px solid ${RED}` }} data-testid={`comms2-fax-retry-card-${f.id}`}>
                <div className="flex items-baseline gap-2">
                  <div className="text-[13px] font-semibold truncate flex-1" style={{ color: NAVY }}>
                    {f.to_name || formatPhoneNumber(f.to_number) || "Unknown"}
                  </div>
                  <div className="text-[11px] tabular-nums shrink-0" style={{ color: AMBER }}>{attempts} / {max}</div>
                </div>
                <div className="text-[11.5px] mt-1 flex items-start gap-1" style={{ color: RED }}>
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>{f.error_message || f.failure_reason || f.status || "failed"}</span>
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  Last try: {lastTry ? new Date(lastTry).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                </div>
                <div className="flex items-center gap-1.5 mt-2 pt-2 border-t" style={{ borderColor: NAVY_HAIRLINE }}>
                  <button
                    onClick={() => retry(f)}
                    disabled={busyId === f.id}
                    className="h-8 px-2.5 rounded-md border text-[11.5px] font-medium hover:bg-slate-50 disabled:opacity-40 flex items-center gap-1"
                    style={{ borderColor: NAVY_HAIRLINE, color: NAVY }}
                    data-testid={`comms2-fax-retry-card-btn-${f.id}`}
                  >
                    {busyId === f.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                    Retry
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
